import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Check, ShieldCheck, Sparkles, MessageCircle, ArrowLeft, MapPin } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { SERVICE_GROUPS } from "@/lib/services";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { setSeo, setJsonLd, clearJsonLd, SITE_URL } from "@/lib/seo";

const DEFAULT_PROVIDES = [
  "A specialist who owns your case end to end",
  "Clear documentation and requirements up front",
  "Remote onboarding — travel is not usually required",
  "Ongoing support after delivery",
];
const SENSITIVE = ["nominee", "citizenship", "dao", "redomicile", "forex", "gaming", "banklicense", "vasp", "vara"];

// Official regulator/authority references for consequential regulatory statements.
// Verified against official sources (2026); shown as external links on the page.
const REFS = {
  einitin: [
    { label: "IRS — Apply for an EIN (free)", href: "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online" },
    { label: "IRS — How to apply for an ITIN", href: "https://www.irs.gov/tin/itin/how-to-apply-for-an-itin" },
    { label: "IRS — ITIN Acceptance Agents", href: "https://www.irs.gov/tin/itin/itin-acceptance-agents" },
  ],
  forex: [
    { label: "CySEC (Cyprus)", href: "https://www.cysec.gov.cy" },
    { label: "FSC Mauritius", href: "https://www.fscmauritius.org" },
  ],
  gaming: [
    { label: "Malta Gaming Authority — B2C licences", href: "https://www.mga.org.mt/licensee-hub/applications/b2c-licences/remote-gaming-services/" },
  ],
  banklicense: [
    { label: "Bank of Lithuania — EMI authorisation", href: "https://www.lb.lt/en/authorisation-of-electronic-money-institutions" },
    { label: "UK FCA", href: "https://www.fca.org.uk" },
  ],
  vasp: [
    { label: "Bank of Lithuania — virtual-asset supervision", href: "https://www.lb.lt/en" },
    { label: "Dubai VARA — licensed activities", href: "https://www.vara.ae/en/licenses-and-register/licensed-activities/" },
  ],
  vara: [
    { label: "VARA — licensed activities", href: "https://www.vara.ae/en/licenses-and-register/licensed-activities/" },
    { label: "VARA rulebooks", href: "https://rulebooks.vara.ae/" },
  ],
};

const Section = ({ title, children }) => (
  <div>
    <h2 className="font-heading text-2xl tracking-tight text-primary">{title}</h2>
    <div className="mt-4">{children}</div>
  </div>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { config } = useSiteConfig();
  // Per-service content: reviewed translation when served, else the live English source.
  const ts = (s, en) => t(`svc.${slug}.${s}`, { defaultValue: en });
  const wa = `https://wa.me/${(config?.whatsapp_number || "85298548459").replace(/\D/g, "")}`;

  const group = SERVICE_GROUPS.find((g) => g.items.some((i) => i.id === slug));
  const item = group?.items.find((i) => i.id === slug);

  useEffect(() => { if (!item) navigate("/more-services", { replace: true }); }, [item, navigate]);
  useEffect(() => {
    if (!item) return;
    const url = SITE_URL + "/service/" + slug;
    const sTitle = t(`nav.svc.${slug.replace(/-/g, "")}.title`, { defaultValue: item.title });
    const sShort = t(`nav.svc.${slug.replace(/-/g, "")}.short`, { defaultValue: item.short });
    setSeo({ title: t("service_ui.seoTitle", { title: sTitle, defaultValue: `${item.title} | UNICORP` }), description: (sShort || ts("body", item.body) || "").slice(0, 158), url, image: "https://images.unsplash.com/photo-1531819177115-428566ccfb50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" });
    setJsonLd("svc-schema", { "@context": "https://schema.org", "@type": "Service", name: sTitle, description: sShort, provider: { "@type": "Organization", name: "UNICORP Services Limited" }, areaServed: "Worldwide" });
    setJsonLd("svc-breadcrumb", { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: t("nav.home"), item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: t("nav.moreServices"), item: SITE_URL + "/more-services" },
      { "@type": "ListItem", position: 3, name: sTitle, item: url } ] });
    if (item.faqs?.length) setJsonLd("svc-faq", { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: item.faqs.map((f, i) => ({ "@type": "Question", name: ts(`faq${i + 1}q`, f.q), acceptedAnswer: { "@type": "Answer", text: ts(`faq${i + 1}a`, f.a) } })) });
    else clearJsonLd("svc-faq");
    return () => { clearJsonLd("svc-schema"); clearJsonLd("svc-breadcrumb"); clearJsonLd("svc-faq"); };
  }, [slug, item, t]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;

  const Icon = item.icon;
  const related = group.items.filter((i) => i.id !== slug && i.id !== "digital-studio").slice(0, 3);
  const provides = item.provides || DEFAULT_PROVIDES;
  const quoteLabel = item.quoteLabel ? ts("quoteLabel", item.quoteLabel) : t("service_ui.requestQuote");
  const sTitle = t(`nav.svc.${slug.replace(/-/g, "")}.title`, { defaultValue: item.title });
  const sShort = t(`nav.svc.${slug.replace(/-/g, "")}.short`, { defaultValue: item.short });

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative grain pt-32 pb-12 border-b border-border bg-secondary/40">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <button onClick={() => navigate("/more-services")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-orange-700 transition-colors mb-5" data-testid="service-back"><ArrowLeft className="h-4 w-4" /> {t("nav.moreServices")}</button>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className={`grid place-items-center h-12 w-12 rounded-sm border ${group.tint}`}><Icon className="h-6 w-6" /></span>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-orange-600 font-mono">{t(`nav.svcGroup.${group.key}`, { defaultValue: group.title })}</p>
            <h1 className="mt-2 font-heading text-4xl sm:text-5xl tracking-tight text-primary" data-testid="service-title">{sTitle}</h1>
            <p className="mt-4 text-muted-foreground text-lg leading-relaxed max-w-2xl">{sShort}.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button size="lg" className="rounded-sm bg-orange-600 text-white hover:bg-orange-700 h-12 px-7" onClick={() => navigate("/contact")} data-testid="service-quote">{quoteLabel} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              <Button size="lg" variant="outline" className="rounded-sm border-slate-300 bg-card hover:bg-secondary h-12 px-6" onClick={() => navigate("/advisor")} data-testid="service-ai"><Sparkles className="h-4 w-4 mr-1.5 text-orange-600" /> {t("common.askAi")}</Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14 space-y-12">
        <Section title={t("service_ui.overview")}>
          <p className="text-muted-foreground leading-relaxed">{ts("body", item.body)}</p>
        </Section>

        {item.whyConsider && (
          <Section title={t("service_ui.whyConsider")}>
            <div className="grid sm:grid-cols-2 gap-3">
              {item.whyConsider.map((p, i) => (
                <div key={i} className="flex items-start gap-3 rounded-sm border border-border bg-card p-4"><Check className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" /><span className="text-sm text-foreground">{ts(`why${i + 1}`, p)}</span></div>
              ))}
            </div>
          </Section>
        )}

        <Section title={t("service_ui.whatProvides")}>
          <div className="grid sm:grid-cols-2 gap-3">
            {provides.map((p, i) => (
              <div key={i} className="flex items-start gap-3 rounded-sm border border-border bg-card p-4"><Check className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" /><span className="text-sm text-foreground">{item.provides ? ts(`provides${i + 1}`, p) : t(`service_ui.provides${i + 1}`, { defaultValue: p })}</span></div>
            ))}
          </div>
        </Section>

        {item.howItWorks && (
          <Section title={t("service_ui.howItWorks")}>
            <div className="space-y-3">
              {item.howItWorks.map((step, i) => (
                <div key={i} className="flex gap-4 rounded-sm border border-border bg-card p-4">
                  <span className="grid place-items-center h-8 w-8 rounded-sm bg-orange-50 text-orange-600 border border-orange-100 shrink-0 font-mono text-sm">{i + 1}</span>
                  <div><p className="text-sm font-medium text-primary">{ts(`hiw${i + 1}t`, step.title)}</p><p className="mt-1 text-sm text-muted-foreground leading-relaxed">{ts(`hiw${i + 1}b`, step.body)}</p></div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {item.jurisdictions && (
          <Section title={t("service_ui.jurisdictions")}>
            <div className="flex flex-wrap gap-2">
              {item.jurisdictions.map((j, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-primary"><MapPin className="h-3.5 w-3.5 text-orange-600" /> {ts(`juris${i + 1}`, j)}</span>
              ))}
            </div>
          </Section>
        )}

        {item.pricing && (
          <Section title={item.pricing.title ? ts("priceTitle", item.pricing.title) : t("service_ui.pricingDefaultTitle")}>
            {item.pricing.headers && item.pricing.rows && (
              <div className="overflow-x-auto rounded-sm border border-border">
                <table className="w-full text-sm" data-testid="service-pricing-table">
                  <thead>
                    <tr className="bg-secondary/60 text-left">
                      {item.pricing.headers.map((h, i) => <th key={i} className="px-4 py-3 font-medium text-primary whitespace-nowrap">{ts(`h${i + 1}`, h)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {item.pricing.rows.map((row, ri) => (
                      <tr key={ri} className="border-t border-border">
                        {row.map((cell, ci) => <td key={ci} className={`px-4 py-3 ${ci === 0 ? "text-primary font-medium" : "text-muted-foreground"}`}>{ts(`r${ri}c${ci}`, cell)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {item.pricing.note && <p className="mt-3 text-xs text-muted-foreground leading-relaxed">{ts("priceNote", item.pricing.note)}</p>}
          </Section>
        )}

        {item.addOns && (
          <Section title={t("service_ui.addOns")}>
            <div className="grid sm:grid-cols-2 gap-3">
              {item.addOns.map((a, i) => (
                <div key={i} className="flex items-start gap-3 rounded-sm border border-border bg-secondary/40 p-4"><Sparkles className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" /><span className="text-sm text-foreground">{ts(`addon${i + 1}`, a)}</span></div>
              ))}
            </div>
          </Section>
        )}

        {item.faqs && (
          <Section title={t("service_ui.faqs")}>
            <div className="space-y-3">
              {item.faqs.map((f, i) => (
                <div key={i} className="rounded-sm border border-border bg-card p-4">
                  <p className="text-sm font-medium text-primary">{ts(`faq${i + 1}q`, f.q)}</p>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{ts(`faq${i + 1}a`, f.a)}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {REFS[slug] && (
          <Section title={t("service_ui.refs")}>
            <div className="flex flex-col gap-2">
              {REFS[slug].map((r, i) => (
                <a key={r.href} href={r.href} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1.5 text-sm text-orange-700 hover:underline w-fit" data-testid={`service-ref-${slug}`}>{ts(`ref${i + 1}`, r.label)} <ArrowRight className="h-3.5 w-3.5 -rotate-45" /></a>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t("service_ui.refsNote")}</p>
          </Section>
        )}

        <div className="rounded-sm border border-border bg-secondary/40 p-5 flex items-start gap-3 text-sm text-muted-foreground">
          <ShieldCheck className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
          <span>{item.compliance
            ? ts("compliance", item.compliance)
            : t(SENSITIVE.includes(slug) ? "service_ui.complianceSensitive" : "service_ui.complianceStandard")}</span>
        </div>

        {related.length > 0 && (
          <Section title={t("service_ui.related")}>
            <div className="grid sm:grid-cols-3 gap-3">
              {related.map((r) => (
                <button key={r.id} onClick={() => navigate("/service/" + r.id)} className="group text-left rounded-sm border border-border bg-card p-4 hover:border-orange-300 transition-colors" data-testid={`service-related-${r.id}`}>
                  <span className="text-sm text-primary group-hover:text-orange-600 transition-colors flex items-center gap-1">{t(`nav.svc.${r.id.replace(/-/g, "")}.title`, { defaultValue: r.title })} <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" /></span>
                  <span className="block text-xs text-muted-foreground mt-1">{t(`nav.svc.${r.id.replace(/-/g, "")}.short`, { defaultValue: r.short })}</span>
                </button>
              ))}
            </div>
          </Section>
        )}

        <div className="rounded-sm bg-primary text-primary-foreground grain px-7 py-10 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight">{t("service_ui.readyTitle", { title: sTitle })}</h2>
          <p className="mt-2 text-primary-foreground/70">{t("service_ui.readySub")}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button size="lg" className="rounded-sm bg-orange-600 text-white hover:bg-orange-500 h-12 px-7" onClick={() => navigate("/contact")} data-testid="service-quote-bottom">{quoteLabel}</Button>
            <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-sm bg-[#25D366] text-white h-12 px-6" data-testid="service-whatsapp"><MessageCircle className="h-5 w-5" /> WhatsApp</a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
